import type { ReactNode } from 'react';
import type { ChargePoint } from '../data/types';
import { freeNow } from '../data/types';

export function Tile({ k, v, d }: { k: string; v: ReactNode; d?: string }) {
  return (
    <div className="tile">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      {d && <div className="d">{d}</div>}
    </div>
  );
}

export function Pill({ children, tone }: { children: ReactNode; tone?: 'on' | 'mid' | 'off' }) {
  return (
    <span className="pill">
      {tone && <span className={`dot ${tone}`} />}
      {children}
    </span>
  );
}

export function Chip({
  label, selected, onClick,
}: { label: ReactNode; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" className="chip" aria-pressed={selected} onClick={onClick}>
      {label}
    </button>
  );
}

/**
 * Free bays over total, as `(3/4)`.
 *
 * Three states, not two. All bays taken is amber and worth waiting for; only a
 * station that is down earns red. Collapsing those loses the distinction an
 * operator actually acts on.
 */
export function ChargerCount({ station }: { station: ChargePoint }) {
  const free = freeNow(station);
  const total = station.connectors.length;
  const down = station.blocked || station.status === 'offline';
  const cls = down ? 'bad' : free > 0 ? 'ok' : 'warn';
  return <span className={`cnt ${cls}`}>({free}/{total})</span>;
}

/** Agent approval and availability. Connectors have their own vocabulary and
 *  their own `connectorTone` — this one used to serve both, which is how a bay
 *  at Preparing ended up sharing a colour with a bay that was not answering. */
export function statusTone(s: string): 'on' | 'mid' | 'off' {
  if (s === 'available' || s === 'Available' || s === 'approved') return 'on';
  if (s === 'offline' || s === 'Offline' || s === 'declined' || s === 'faulted') return 'off';
  return 'mid';
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '26px 16px' }}>
      <div className="li-title">{title}</div>
      {hint && <div className="li-sub" style={{ marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

export function Loading() {
  return <div className="card faint" style={{ padding: 22, textAlign: 'center' }}>Loading…</div>;
}
